import type { ContactDto, ContactMutationOutput } from '@fos/shared/contracts/crm';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { toast } from '../../../ui/Toast';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormState {
  name: string;
  email: string;
  phone: string;
  role: string;
  isPrimary: boolean;
}

type Errors = Partial<Record<keyof FormState | 'form', string>>;

function initial(contact: ContactDto | null | undefined, firstContact: boolean): FormState {
  return {
    name: contact?.name ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
    role: contact?.role ?? '',
    isPrimary: contact ? contact.isPrimary : firstContact,
  };
}

/**
 * Dodanie / edycja osoby kontaktowej (MVP §5: imię, email, telefon opcjonalnie, rola, kontakt główny).
 * Ten sam email u innego klienta nie blokuje zapisu: po zapisie pokazujemy ostrzeżenie (konflikt do rozstrzygnięcia).
 */
export function ContactFormDialog({
  open,
  onOpenChange,
  clientId,
  contact,
  firstContact = false,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  contact?: ContactDto | null;
  /** klient nie ma jeszcze kontaktów: domyślnie zaznacz „Kontakt główny” */
  firstContact?: boolean;
  onSaved?: (result: ContactMutationOutput) => void;
}) {
  const utils = trpc.useUtils();
  const [form, setForm] = useState<FormState>(() => initial(contact, firstContact));
  const [errors, setErrors] = useState<Errors>({});
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);
  const create = trpc.clients.contacts.create.useMutation();
  const update = trpc.clients.contacts.update.useMutation();
  const pending = create.isPending || update.isPending;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu
  useEffect(() => {
    if (!open) return;
    setForm(initial(contact, firstContact));
    setErrors({});
  }, [open]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  async function submit(event: FormEvent) {
    event.preventDefault();
    const e: Errors = {};
    if (!form.name.trim()) e.name = 'Podaj imię i nazwisko';
    if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) e.email = 'Nieprawidłowy adres email';
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    const fields = {
      name: form.name.trim(),
      email: form.email.trim() ? form.email.trim() : null,
      phone: form.phone.trim(),
      role: form.role.trim(),
      isPrimary: form.isPrimary,
    };
    try {
      const result = contact
        ? await update.mutateAsync({ id: contact.id, version: contact.version, ...fields })
        : await create.mutateAsync({ clientId, ...fields });
      await utils.clients.invalidate();
      const warning = result.warnings.find((w) => w.code === 'EMAIL_CONFLICT');
      if (warning) {
        toast.warning(contact ? 'Zapisano kontakt' : 'Dodano kontakt', { description: warning.message });
      } else {
        toast.success(contact ? 'Zapisano kontakt' : 'Dodano kontakt');
      }
      onOpenChange(false);
      onSaved?.(result);
    } catch (err) {
      if (appErrorCode(err) === 'CONFLICT') {
        setErrors({ form: 'Ktoś w międzyczasie zmienił ten kontakt. Zamknij okno i spróbuj ponownie.' });
        return;
      }
      const issues = validationIssues(err);
      if (issues.length > 0) {
        const next: Errors = {};
        for (const issue of issues) {
          const key = (['name', 'email', 'phone', 'role'] as const).find((k) => k === issue.path) ?? 'form';
          next[key] ??= issue.message;
        }
        setErrors(next);
        return;
      }
      setErrors({ form: errorMessage(err) });
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !pending && onOpenChange(next)}
      title={contact ? 'Edytuj kontakt' : 'Nowy kontakt'}
      width="m"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="contact-form" loading={pending}>
            {contact ? 'Zapisz' : 'Dodaj kontakt'}
          </Button>
        </>
      }
    >
      <form id="contact-form" ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4">
        {errors.form && <FormMessage>{errors.form}</FormMessage>}
        <Field label="Imię i nazwisko" error={errors.name}>
          <Input value={form.name} onChange={(e) => set('name', e.target.value)} autoFocus maxLength={200} />
        </Field>
        <Field label="Email" error={errors.email}>
          <Input
            type="email"
            inputMode="email"
            autoComplete="off"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            placeholder="anna@firma.pl"
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Telefon (opcjonalnie)" error={errors.phone}>
            <Input
              type="tel"
              inputMode="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              maxLength={40}
            />
          </Field>
          <Field label="Rola" error={errors.role}>
            <Input
              value={form.role}
              onChange={(e) => set('role', e.target.value)}
              placeholder="np. Marketing"
              maxLength={100}
            />
          </Field>
        </div>
        <Checkbox
          checked={form.isPrimary}
          onCheckedChange={(v) => set('isPrimary', v)}
          label="Kontakt główny"
          disabled={Boolean(contact?.archivedAt)}
        />
      </form>
    </Dialog>
  );
}
